import { VariableType } from '@/constants';
import { JSONSchema } from '@/features/variable/settings/validation';
import {
  ContextualRule,
  VariableInfo,
  VariableMetadata,
} from '@/lib/PlannerDTOs';
import { parse } from '@/utils/json';

function hasValidationContent(rule: ContextualRule): boolean {
  return (
    (rule?.allowedValues?.length ?? 0) > 0 ||
    rule?.minimumLength != null ||
    rule?.maximumLength != null ||
    rule?.minimumValue != null ||
    rule?.maximumValue != null ||
    (rule?.jsonSchema != null && rule?.jsonSchema !== '{}')
  );
}

export function hasAnyValidationConfigured(
  variable: VariableInfo | null,
): boolean {
  if (variable?.metadata?.jsonSchema && variable?.metadata?.jsonSchema !== '{}')
    return true;

  const rules = variable?.metadata?.contextualRules;
  return !!rules?.some((rule) => hasValidationContent(rule));
}

export function needsContextualRulesException(
  variable: VariableInfo | null,
): boolean {
  if (
    variable?.type === VariableType.BOOLEAN ||
    variable?.complexity?.guardrails?.validation?.exceptionRaised
  ) {
    return false;
  }

  const exceptionReason =
    variable?.complexity?.guardrails?.validation?.exceptionReason;
  if (exceptionReason) return false;

  if (variable?.metadata?.jsonSchema && variable?.metadata?.jsonSchema !== '{}')
    return false;

  const rules = variable?.metadata?.contextualRules;

  const hasContextualRulesWithConditions = rules?.some(
    (rule) => rule.contextualRulesConditions?.length,
  );
  if (hasContextualRulesWithConditions) return false;

  const hasDefaultRuleWithContent = rules?.some(
    (rule) =>
      !rule.contextualRulesConditions?.length && hasValidationContent(rule),
  );
  if (hasDefaultRuleWithContent) return false;

  return true;
}

export function isContextualRulesValid(variable: VariableInfo | null): boolean {
  const rules = variable?.metadata?.contextualRules;
  if (!rules?.length) return true;

  const hasAnyContent = rules.some((rule) => hasValidationContent(rule));
  if (!hasAnyContent) return false;

  const isStringType = [VariableType.STRING, VariableType.FILE].includes(
    variable?.type as VariableType,
  );

  return rules.every((rule) => {
    if (!hasValidationContent(rule)) return true;

    if ((rule.allowedValues?.length ?? 0) > 0) {
      if (isStringType) {
        const values = rule.allowedValues as string[];
        const emptyCount = values.filter((v) => v === '').length;
        if (emptyCount > 1) return false;
        const nonEmpty = values.filter((v) => v !== '');
        if (new Set(nonEmpty).size < nonEmpty.length) return false;
      } else {
        const values = rule.allowedValues as Array<number | null>;
        const emptyOrZeroCount = values.filter(
          (v) => v == null || v === 0,
        ).length;
        if (emptyOrZeroCount > 1) return false;
        const meaningful = values.filter((v) => v != null && v !== 0);
        if (new Set(meaningful).size < meaningful.length) return false;
      }
    }

    return true;
  });
}

const blacklistKeys = [
  'version',
  'assignBy',
  'criticality',
  'enabled',
  'scriptable',
  'lastRolloutUpdatedTime',
  'schema',
  'RestrictedBy',
  'schedulingDefinition',
];

const arrayKeys = ['facets', 'subscribedServices'];

export enum State {
  NONE = 'none',
  ADDED = 'added',
  MODIFIED = 'modified',
  DELETED = 'deleted',
}

export const isArrayModified = (
  currentValue: Array<any>,
  valueToCompare: Array<any>,
) => {
  if (!currentValue?.length && !valueToCompare?.length) return State.NONE;

  if (
    (!currentValue?.length && valueToCompare?.length > 0) ||
    (currentValue?.length > 0 && !valueToCompare?.length)
  )
    return State.MODIFIED;

  const currentValueRemoved = currentValue.filter((value) => {
    return !valueToCompare.includes(value);
  });

  const valueToCompareRemoved = valueToCompare.filter((value) => {
    return !currentValue.includes(value);
  });

  if (currentValueRemoved?.length || valueToCompareRemoved?.length)
    return State.MODIFIED;

  return State.NONE;
};

const isValueModified = (currentValue: any, valueToCompare: any) => {
  if (currentValue == null && valueToCompare == null) return State.NONE;

  if (typeof currentValue === 'string' || typeof valueToCompare === 'string') {
    if ((!currentValue && !valueToCompare) || currentValue === valueToCompare)
      return State.NONE;
    else return State.MODIFIED;
  }

  if (currentValue !== valueToCompare) return State.MODIFIED;

  return State.NONE;
};

const compareObj = (
  currentObj: VariableInfo | JSONSchema,
  toCompare: VariableInfo | JSONSchema,
  objToUpdate: VariableInfo | JSONSchema,
) => {
  Object.keys(objToUpdate).forEach((key) => {
    if (blacklistKeys.includes(key)) {
      delete objToUpdate[key];
      return false;
    }

    const currentValue = currentObj?.[key];
    const valueToCompare = toCompare?.[key];

    if (key === 'metadata') {
      compareObj(currentValue, valueToCompare, objToUpdate['metadata']);

      if (!Object.keys(objToUpdate['metadata']).length)
        delete objToUpdate['metadata'];

      return;
    }

    if (arrayKeys.includes(key)) {
      objToUpdate[key] = isArrayModified(currentValue, valueToCompare);

      if (objToUpdate[key] === State.NONE) {
        delete objToUpdate[key];
        return;
      }

      return;
    }

    objToUpdate[key] = isValueModified(currentValue, valueToCompare);

    if (objToUpdate[key] === State.NONE) delete objToUpdate[key];
  });
};

export const getVariableDiff = (
  target: VariableInfo,
  compare: VariableInfo,
) => {
  const variableDiff = new VariableInfo();

  variableDiff.metadata = new VariableMetadata();

  compareObj(target, compare, variableDiff);

  return variableDiff;
};

export const getSchemaDiff = (target: VariableInfo, compare: VariableInfo) => {
  const schemaDiff = new JSONSchema();
  const targetSchema = target?.metadata?.jsonSchema
    ? (parse(target?.metadata?.jsonSchema) as JSONSchema)
    : new JSONSchema();
  const compareSchema = compare?.metadata?.jsonSchema
    ? (parse(compare?.metadata?.jsonSchema) as JSONSchema)
    : new JSONSchema();

  compareObj(targetSchema, compareSchema, schemaDiff);

  return schemaDiff;
};

export const isVariableModified = (target, compare) => {
  const variableDiff = getVariableDiff(target, compare);

  return Object.keys(variableDiff).length > 0;
};

export const supportsValidation = (type: VariableType) => {
  // Check if type is a valid VariableType
  if (!Object.values(VariableType).includes(type)) {
    return false;
  }

  return type !== VariableType.BOOLEAN;
};

export const isRolloutPage = (pathname: string) => {
  if (!pathname) return false;
  return /\/rollout(\/|$)/.test(pathname);
};

export const isRolloutChangelogPage = (pathname: string) => {
  if (!pathname) return false;
  return /\/rollout\/changelog(\/|$)/.test(pathname);
};

/**
 * Returns the pathname to navigate to for variable settings (e.g. edit).
 * Handles rollout, rollout changelog, and variable list page path structures.
 */
export const getVariableEditPathname = (
  pathname: string,
  variableName: string,
  action: string,
) => {
  if (isRolloutChangelogPage(pathname)) {
    const basePath = pathname.replace('/changelog', '');
    return `${basePath}/${action}`;
  }
  if (!isRolloutPage(pathname)) {
    return `${pathname}/${variableName}/${action}`;
  }
  return `${pathname}/${action}`;
};

export const isFieldChanged = (difference, pathSeparatedBySlash: string) => {
  if (!pathSeparatedBySlash || !difference) return false;

  const paths = pathSeparatedBySlash.split('/');

  let value: null | boolean;

  paths.forEach((path: string) => {
    if (!value) {
      value = difference?.[path];

      return;
    }

    value = value?.[path];
  });

  return value;
};
